import React, {useEffect, useRef, useState} from "react";
import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    List,
    ListItem,
    Spinner,
    Text,
    useColorModeValue,
    useOutsideClick,
} from "@chakra-ui/react";
import {CloseButton} from "@chakra-ui/react";
import {FiSearch} from "react-icons/fi";
import {searchSchool} from "../SchoolSetup/Services";

// Searchable school picker backed by the existing school search API. Debounces
// the query, shows a results dropdown, and reports the chosen school upward.
function SchoolSearchSelect({selectedSchool, onSelect, placeholder = "Search school by name...", isInvalid = false}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useOutsideClick({ref: containerRef, handler: () => setOpen(false)});

    const dropdownBg = useColorModeValue("white", "gray.700");
    const hoverBg = useColorModeValue("gray.100", "whiteAlpha.200");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
    const subColor = useColorModeValue("gray.500", "gray.400");

    // Debounced search while the dropdown is open.
    useEffect(() => {
        if (!open) return undefined;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await searchSchool({name: query || "", code: "", status: ""}, 1, 10);
                if (response.status === 200) {
                    setResults(response.data?.schoolList || []);
                }
            } catch (e) {
                console.error("Error while searching schools e:", e);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [query, open]);

    const handleSelect = (school) => {
        onSelect(school);
        setQuery("");
        setOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onSelect(null);
        setQuery("");
    };

    return (
        <Box position="relative" ref={containerRef}>
            <InputGroup>
                <InputLeftElement pointerEvents="none" children={<FiSearch color="gray"/>}/>
                <Input
                    value={open ? query : (selectedSchool?.name || "")}
                    placeholder={placeholder}
                    isInvalid={isInvalid}
                    onFocus={() => setOpen(true)}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    borderRadius="8px"
                />
                {selectedSchool?.name && !open && (
                    <InputRightElement>
                        <CloseButton size="sm" onClick={handleClear}/>
                    </InputRightElement>
                )}
            </InputGroup>

            {open && (
                <Box
                    position="absolute"
                    zIndex={30}
                    mt="4px"
                    w="100%"
                    bg={dropdownBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="8px"
                    boxShadow="lg"
                    maxH="240px"
                    overflowY="auto"
                >
                    {loading ? (
                        <Flex justify="center" align="center" py="14px">
                            <Spinner size="sm" color="blue.400"/>
                        </Flex>
                    ) : results.length === 0 ? (
                        <Text px="12px" py="10px" fontSize="sm" color={subColor}>
                            No schools found.
                        </Text>
                    ) : (
                        <List>
                            {results.map((school) => (
                                <ListItem
                                    key={school.id}
                                    px="12px"
                                    py="8px"
                                    cursor="pointer"
                                    _hover={{bg: hoverBg}}
                                    onClick={() => handleSelect(school)}
                                >
                                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                                        {school.name}
                                    </Text>
                                    <Text fontSize="xs" color={subColor} noOfLines={1}>
                                        {school.channelCode ? `Channel: ${school.channelCode}` : (school.address || "")}
                                    </Text>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default SchoolSearchSelect;
